import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useTranslations } from "use-intl";

import { Card, CardContent, CardHeader, CardTitle } from "@lawline/ui/components/card";
import { Skeleton } from "@lawline/ui/components/skeleton";

import { compactNumberFmt, formatMoney, formatMoneyCompact } from "@platform/fmt";
import { telemetryQueryOptions } from "./telemetry.queries";
import { TelemetryChart } from "./telemetry-chart";

// lawline tokens is constructed to cost $1 per 1_000_000 tokens
const USD_PER_LAWLINE_TOKEN = 1 / 1_000_000;

// A fixed rate is enough, because the telemetry figures are estimates.
const USD_TO_SEK = 10.0;

// Percentage change vs the previous period, green when up, red when down.
function Delta({ current, previous }: { current: number; previous: number }) {
	if (previous === 0) return null;
	const pct = ((current - previous) / previous) * 100;
	const up = pct > 0;
	const Icon = up ? ArrowUp : ArrowDown;
	return (
		<span
			className={`flex items-center gap-0.5 text-sm font-medium ${
				up ? "text-green-600 dark:text-green-500" : "text-destructive"
			}`}
		>
			<Icon className="size-3.5" />
			{Math.abs(pct).toFixed(1)}%
		</span>
	);
}

export function AdminTelemetryPage() {
	const t = useTranslations("admin.telemetry");
	const { data, error } = useQuery(telemetryQueryOptions);

	const rateCents = data?.rateCents ?? 0;
	const currency = data?.currency ?? "sek";
	const toRevenue = (lawline: number) => (lawline * rateCents) / 100;

	// Revenue generated per day, partitioned by Stripe-pipeline state (no
	// overlap): receivable = reported to Stripe, pending = not yet reported.
	const money = (n: number) => formatMoneyCompact(n, currency);
	// Internal cost estimate from usage_events. The lawline token is pegged at
	// USD 1 per 1M by construction.
	const costSek = (lawline: number) => lawline * USD_PER_LAWLINE_TOKEN * USD_TO_SEK;
	const moneySek = (n: number) => formatMoneyCompact(n, "sek");
	const revenuePoints =
		data?.series.map((p) => ({
			t: p.t,
			cost: costSek(p.lawline),
			receivable: toRevenue(p.metered),
			pending: toRevenue((p.billable ?? p.lawline) - p.metered),
		})) ?? [];

	return (
		<section className="mx-auto mt-2 flex w-full max-w-3xl flex-col gap-6 overflow-x-hidden">
			<h1 className="text-2xl font-semibold">{t("title")}</h1>

			{error ? (
				<p className="text-destructive text-sm">{t("error")}</p>
			) : !data ? (
				<Skeleton className="h-24 w-full" />
			) : (
				<>
					<div className="grid grid-cols-3 gap-4">
						<Card>
							<CardHeader>
								<CardTitle className="text-muted-foreground text-sm font-medium">
									{t("stats.tenants")}
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="flex items-baseline gap-2">
									<p className="font-mono text-2xl font-semibold tabular-nums">
										{data.totalTenants}
									</p>
									<Delta current={data.totalTenants} previous={data.previousTenants} />
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle className="text-muted-foreground text-sm font-medium">
									{t("stats.users")}
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="flex items-baseline gap-2">
									<p className="font-mono text-2xl font-semibold tabular-nums">{data.totalUsers}</p>
									<Delta current={data.totalUsers} previous={data.previousUsers} />
								</div>
							</CardContent>
						</Card>
						<Card>
							<CardHeader>
								<CardTitle className="text-muted-foreground text-sm font-medium">
									{t("stats.received30d")}
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="flex items-baseline gap-2">
									<p className="font-mono text-2xl font-semibold tabular-nums">
										{formatMoney(data.received.current, data.received.currency)}
									</p>
									<Delta current={data.received.current} previous={data.received.previous} />
								</div>
							</CardContent>
						</Card>
					</div>

					<TelemetryChart
						title={t("charts.revenue")}
						points={revenuePoints}
						series={[
							{
								key: "receivable",
								label: t("charts.series.receivable"),
								color: "var(--chart-1)",
								stackId: "rev",
								format: money,
							},
							{
								key: "pending",
								label: t("charts.series.pending"),
								color: "var(--chart-2)",
								stackId: "rev",
								format: money,
							},
							{
								key: "cost",
								label: t("charts.series.cost"),
								color: "var(--chart-4)",
								format: moneySek,
							},
						]}
					/>
					<TelemetryChart
						title={t("charts.requests")}
						points={data.series}
						series={[
							{
								key: "requests",
								label: t("charts.series.requests"),
								color: "var(--chart-2)",
								format: (n) => compactNumberFmt.format(n),
							},
						]}
					/>
				</>
			)}
		</section>
	);
}
